import { Component, ChangeDetectionStrategy, computed, inject, effect } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatRadioModule } from '@angular/material/radio';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatIconModule } from '@angular/material/icon';
import { ContactService } from '../../services/contact.service';
import { FeatureFlagService } from '../../services/feature-flag.service';
import { SiteConfigService } from '../../services/site-config.service';
import { ContactFormPayload, SITE_TYPE_OPTIONS } from '../../models/contact-form.model';

@Component({
  selector: 'app-contact-cta',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatRadioModule,
    MatCheckboxModule,
    MatButtonModule,
    MatIconModule,
  ],
  templateUrl: './contact-cta.component.html',
  styleUrl: './contact-cta.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContactCtaComponent {
  private readonly fb = inject(FormBuilder);
  private readonly contactService = inject(ContactService);
  private readonly siteConfig = inject(SiteConfigService);
  private readonly featureFlags = inject(FeatureFlagService);
  private readonly snackBar = inject(MatSnackBar);

  readonly status = this.contactService.status;
  readonly section = this.siteConfig.contact;
  readonly siteTypeOptions = computed(() => {
    this.featureFlags.flags();
    return this.featureFlags.filterContactSiteTypes(SITE_TYPE_OPTIONS);
  });

  readonly form = this.fb.nonNullable.group({
    name: ['', Validators.required],
    companyName: [''],
    email: ['', [Validators.required, Validators.email]],
    phone: [''],
    siteType: ['', Validators.required],
    hasExistingWebsite: [''],
    message: [''],
    privacyConsent: [false, Validators.requiredTrue],
    website: [''],
  });

  constructor() {
    effect(() => {
      const current = this.status();
      const fallbackEmail = this.section()?.errorFallbackEmail ?? 'info@allindev.hu';
      if (current === 'success') {
        this.snackBar.open('Köszönjük! Hamarosan felvesszük veled a kapcsolatot.', 'Bezár', {
          panelClass: 'success-snackbar',
          duration: 6000,
        });
        this.form.reset();
        this.contactService.reset();
      } else if (current === 'error') {
        this.snackBar.open(
          `Sajnos hiba történt. Kérjük, próbáld meg újra, vagy írj közvetlenül a ${fallbackEmail} címre.`,
          'Bezár',
          { panelClass: 'error-snackbar', duration: 8000 },
        );
        this.contactService.reset();
      }
    });
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.contactService.submit(this.form.getRawValue() as ContactFormPayload);
  }
}
